<template>
  <modal
    :title="isEditView ? i18n.settingsTitle : i18n.title"
    :show="show"
    :hide-footer="true"
    @hidden="hideModal"
  >
    <div class="text-secondary text-normal">
      {{ i18n.description }}
    </div>
    <form @submit.prevent="onSubmit">
      <div class="d-flex flex-column my-4" v-if="isNestedTeamsFeatureEnabled">
        <label class="text-normal">Parent Org</label>
        <org-selector-dropdown
          class="mx-n2"
          :selected="parentTeam"
          @change="onParentTeamChange"
          :org-id="orgId"
          :disabled="false"
        />
      </div>
      <div class="d-flex flex-column my-4">
        <label class="text-normal">{{ i18n.nameLabel }}</label>
        <input
          class="input-field text-normal team-name mx-n2"
          :class="{ 'errored-field': errors.name }"
          type="text"
          v-model="formData.name"
          v-autofocus
          v-no-capture
        />
        <template v-if="errors.name">
          <div v-for="(error, index) in errors.name" :key="index" class="field-error-message">
            {{ error }}
          </div>
        </template>
        <span class="text-secondary mt-2">{{ i18n.nameExamples }}</span>
      </div>
      <div class="d-flex flex-column my-4" v-if="showTrackSelector">
        <label class="text-normal">{{ i18n.trackLabel }}</label>
        <div class="d-flex flex-gap-14">
          <div class="track-checkbox" :class="{ 'errored-field': errors.track }">
            <b-form-checkbox v-model="preferences.enableGoals" name="check-button" switch>
              Goals
            </b-form-checkbox>
          </div>
          <div class="track-checkbox" :class="{ 'errored-field': errors.track }">
            <b-form-checkbox v-model="preferences.enableProjects" name="check-button" switch>
              Projects
            </b-form-checkbox>
          </div>
        </div>
        <template v-if="errors.track">
          <div v-for="(error, index) in errors.track" :key="index" class="field-error-message">
            {{ error }}
          </div>
        </template>
      </div>
      <div class="mb-4">
        <label class="pb-1 d-flex align-items-baseline"
          >{{ i18n.permissionHeading }} <span class="locked-icon ml-1"></span
        ></label>
        <b-form-radio v-model="formData.isPrivate" name="permissions" :value="false" class="pb-2">
          <div class="bold access-modifier">{{ i18n.public }}</div>
          <div>{{ i18n.publicDescription }}</div>
        </b-form-radio>
        <b-form-radio
          v-model="formData.isPrivate"
          name="permissions"
          :value="true"
          :disabled="!isPrivateTeamsAllowed"
        >
          <div class="bold access-modifier d-flex align-items-center">
            {{ i18n.private }}
            <upgrade-tag
              :org-id="orgId"
              class="ml-2"
              v-if="!isPrivateTeamsAllowed"
              @click="onUpgradeClick"
            >
              <div slot="heading">
                Keep things private
              </div>
              <div slot="content">
                Private teams and their Goals are only visible to members of the team. Only team
                owners can add new users to a private team.
              </div>
            </upgrade-tag>
          </div>
          <div>{{ i18n.privateDescription }}</div>
        </b-form-radio>
      </div>
      <template v-if="isNestedTeamsFeatureEnabled && isEditView">
        <div v-if="groupType === GROUP_TYPE.CHILDORG" class="mb-4">
          <b-form-checkbox
            :checked="!formData.isOrgLike"
            @input="formData.isOrgLike = !$event"
            switch
          >
            {{ i18n.conversion }}
          </b-form-checkbox>
        </div>
        <div v-else class="mb-4">
          <b-form-checkbox v-model="formData.isOrgLike" switch
            >{{ i18n.conversion }}
          </b-form-checkbox>
        </div>
      </template>
      <div v-if="typeof errors === 'string'" class="field-error-message">
        {{ errors }}
      </div>
      <submit-buttons
        class="my-3"
        :is-loading="isSubmitting"
        :submit-text="isEditView ? i18n.saveBtnTitle : i18n.createBtnTitle"
        @save="onSubmit"
        @cancel="hideModal"
      >
        <div class="d-flex align-items-center" v-if="teamId">
          <spinner v-if="isArchiving" class="mr-2" />
          <b-button
            variant="link"
            class="text-secondary text-medium"
            @click="triggerConfirmation('unarchive')"
            v-else-if="team && team.isArchived"
          >
            {{ i18n.unarchive }}
          </b-button>
          <b-button
            variant="link"
            class="text-secondary text-medium"
            @click="triggerConfirmation('archive')"
            v-else
          >
            {{ i18n.archive }}
          </b-button>
          <b-button
            variant="link text-error"
            class="text-secondary text-medium"
            @click="triggerConfirmation('delete')"
            >{{ i18n.delete }}
          </b-button>
        </div>
      </submit-buttons>
    </form>
    <confirm-dialog
      ref="confirmModal"
      :title="confirmationMessage.title"
      @ok="onConfirmation"
      :ok-title="confirmationMessage.ok"
      :ok-variant="action === 'delete' ? 'danger' : 'primary'"
      :cancel-title="i18n.cancelConfirmationTitle"
    >
      {{ confirmationMessage.body }}
    </confirm-dialog>
  </modal>
</template>

<script>
import ConfirmDialog from '@/components/ConfirmDialog'
import OrgSelectorDropdown from '@/components/goal/OrgSelectorDropdown'
import Modal from '@/components/Modal'
import UpgradeTag from '@/components/paywalls/UpgradeTag.vue'
import Spinner from '@/components/Spinner'
import SubmitButtons from '@/components/SubmitButtons'
import { GROUP_MAP, GROUP_TYPE } from '@/constants/entities'
import { featureFlags } from '@/constants/featureFlags'
import { goalsTabIds } from '@/constants/goals'
import { PROJECT_PALETTE } from '@/constants/project'
import { ROUTE_NAMES } from '@/constants/routes'
import { DEFAULT_PREFERENCE } from '@/constants/team'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import TeamMixin from '@/mixins/TeamMixin'
import { isCtrlEvent } from '@/utils/kbEvents'
import { getTeamPageRouteConfig } from '@/utils/navigationConfigs'
import { BButton, BFormCheckbox, BFormRadio } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

const childOrg = GROUP_MAP[GROUP_TYPE.CHILDORG].name
const team = GROUP_MAP[GROUP_TYPE.TEAM].name

const i18n = {
  [GROUP_TYPE.CHILDORG]: {
    entityLabel: childOrg,
    description: `Use ${childOrg}s to create hierarchy under your main ORG, These could be Business units po product lines under your main Org`,
    nameLabel: `${childOrg} name`,
    nameExamples: 'e.g. Product X',
    publicDescription: 'Anyone in the ORG can see and edit goals in this child-org ',
    privateDescription: 'Only member can see and edit goals, Org admins can manage this team.',
    conversion: 'Convert to team',
    archive: {
      title: `Are you sure you want to archive this ${childOrg}?`,
      body: `You can always re-open a closed ${childOrg}`,
      ok: 'Yes, archive'
    },
    unarchive: {
      title: `Restore this ${childOrg}?`,
      body: "We'll move it to the same Org it belonged to",
      ok: 'Yes, restore'
    },
    delete: {
      title: `Delete this ${childOrg}?`,
      body: `Deleting this ${childOrg} will delete all related Goals and KRs. This action cannot be undone.`,
      ok: 'Yes, delete'
    }
  },
  [GROUP_TYPE.TEAM]: {
    entityLabel: team,
    description: `${team} could be cross-functional, like Product and Marketing working together or a single function such as Engineering.`,
    nameExamples: 'e.g. Product Falcon, Marketing, Partnerships',
    publicDescription: `Anyone can see and edit goals of this ${team}`,
    privateDescription: `Only ${team} members can see and edit goals`,
    conversion: 'Convert to child org',
    archive: {
      title: 'Are you sure you want to archive this Team?',
      body: 'You can always re-open a closed Team',
      ok: 'Yes, archive'
    },
    unarchive: {
      title: `Restore this ${team}?`,
      body: "We'll move it to the same Org it belonged to",
      ok: 'Yes, restore'
    },
    delete: {
      title: `Delete this ${team}?`,
      body: `Deleting this ${team} will delete all related Goals and KRs. This action cannot be undone.`,
      ok: 'Yes, delete'
    }
  },
  common: {
    saveBtnTitle: 'Save Changes',
    cancelConfirmationTitle: 'No, I changed my mind',
    archive: 'Archive',
    unarchive: 'Unarchive',
    delete: 'Delete',
    public: 'Public',
    private: 'Private'
  },
  dynamic(entityLabel) {
    return {
      title: `Add a new ${entityLabel}`,
      settingsTitle: `${entityLabel} Settings`,
      nameLabel: `${entityLabel} name`,
      trackLabel: `Use this ${entityLabel} to track`,
      permissionHeading: `${entityLabel} permissions`,
      createBtnTitle: `Add this ${entityLabel.toLowerCase()}`
    }
  }
}

/**
 * This component is used to create/edit/delete/archive a team
 */
export default {
  name: 'ManageTeam',
  props: ['show', 'teamId', 'orgId', 'groupType', 'subOrgId', 'meta', 'handleNavigationOnDelete'],
  model: {
    prop: 'show'
  },
  components: {
    BFormRadio,
    BFormCheckbox,
    Modal,
    SubmitButtons,
    BButton,
    ConfirmDialog,
    Spinner,
    OrgSelectorDropdown,
    UpgradeTag
  },
  mixins: [TeamMixin, EventBusMixin, FeaturesMixin],
  data() {
    const entityLabel = i18n[this.groupType].entityLabel
    const team = (this.teamId && this.$store.getters.teamById(this.teamId)) || this.meta
    const isEditView = Boolean(this.teamId)
    return {
      formData: {
        isOrgLike: this.groupType === GROUP_TYPE.CHILDORG,
        isPrivate: team ? team.isPrivate : false,
        name: team?.name,
        parentId: team?.parentId ?? this.subOrgId
      },
      errors: {},
      activeModal: null,
      confirmationMessage: {},
      action: null,
      isArchiving: false,
      isSubmitting: false,
      i18n: { ...i18n[this.groupType], ...i18n.common, ...i18n.dynamic(entityLabel) },
      isNestedTeamsFeatureEnabled: this.getFeatureFlagValue(
        this.orgId,
        featureFlags.ALLOW_NESTED_TEAMS
      ),
      isPrivateTeamsAllowed: true,
      GROUP_TYPE,
      preferences: {
        enableProjects: isEditView
          ? team?.preferences.enableProjects
          : DEFAULT_PREFERENCE.enableProjects,
        enableGoals: isEditView ? team?.preferences.enableGoals : DEFAULT_PREFERENCE.enableGoals
      },
      isEditView
    }
  },
  computed: {
    team() {
      return this.$store.getters.teamById(this.teamId) || this.meta
    },
    parentTeam() {
      return this.$store.getters.teamById(this.formData.parentId)
    },
    isProjectsEnabled() {
      return !!this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_PROJECTS)
    },
    showTrackSelector() {
      return this.isProjectsEnabled && this.groupType === GROUP_TYPE.TEAM
    }
  },
  methods: {
    ...mapActions(['getTeam']),
    ...mapActions(['createTeam', 'editTeam']),
    ...mapActions('project', ['createProject']),
    ...mapActions('starred', ['getStarred']),
    async onSubmit() {
      try {
        this.isSubmitting = true
        const payload = {
          ...this.formData,
          preferences: this.preferences
        }
        if (this.isEditView) {
          const team = await this.editTeam({ orgId: this.orgId, teamId: this.teamId, ...payload })
          if (!team.preferences.enableProjects) {
            this.getStarred(team.organizationId)
          }
          EventBus.$emit('ADD_TOAST', { message: 'Changes have been saved successfully' })
          this.hideModal()
        } else {
          const team = await this.createTeam({ orgId: this.orgId, ...payload })
          if (this.isProjectsEnabled && team.preferences.enableProjects) {
            await this.createProject({
              orgId: team.organizationId,
              payload: {
                title: 'Project 1',
                color: PROJECT_PALETTE[0],
                teamId: team.id
              }
            })
          }
          EventBus.$emit('ADD_TOAST', { message: 'Team has been created successfully' })

          this.hideModal()
          /**
           * @emits
           * This is emitted when a new team is created
           */
          this.$emit('add', team)
        }
      } catch (e) {
        this.errors =
          R.path(['response', 'data', 'detail'], e) || R.path(['response', 'data', 'message'], e)
      }
      this.isSubmitting = false
    },
    triggerConfirmation(action) {
      this.action = action
      this.confirmationMessage = i18n[this.groupType][action]
      this.$refs.confirmModal.$refs.dialog.show()
    },
    async onConfirmation() {
      if (this.action === 'archive') {
        this.isArchiving = true
        const inArchived = await this.performTeamArchival({
          action: 'archive',
          teamId: this.teamId,
          orgId: this.orgId
        })
        this.isArchiving = false
        if (inArchived) {
          this.$emit('team-archived')
        }
      } else if (this.action === 'unarchive') {
        this.isArchiving = true
        const inUnarchived = await this.performTeamArchival({
          action: 'unarchive',
          teamId: this.teamId,
          orgId: this.orgId
        })
        this.isArchiving = false
        if (inUnarchived) {
          this.$emit('team-unarchived')
        }
      } else if (this.action === 'delete') {
        const isDeleted = await this.performTeamDeletion({ teamId: this.teamId, orgId: this.orgId })
        if (isDeleted) {
          this.hideModal()
          if (this.handleNavigationOnDelete) {
            if (this.formData.isOrgLike) {
              await this.$store.dispatch('orgSelect/clear')
              this.$router.push({
                name: ROUTE_NAMES.ORG_SELECT
              })
            } else {
              const location = getTeamPageRouteConfig({
                orgId: this.orgId,
                entity: goalsTabIds.goals,
                teamId: this.subOrgIdCopy
              })
              this.$router.push(location)
            }
          }
        }
      }
    },
    onParentTeamChange(team) {
      this.formData.parentId = team.id
    },
    hideModal() {
      this.$emit('input', false)
    },
    onUpgradeClick(event) {
      !isCtrlEvent(event) && this.hideModal()
    }
  },
  async mounted() {
    this.listenToEventBus('SUBMIT_FORM', () => {
      this.show && this.onSubmit()
    })
    // keeping a copy because this.subOrgId becomes null after deletion of team. We need it for navigation to Your work page upon team deletion
    this.subOrgIdCopy = this.subOrgId
    this.isPrivateTeamsAllowed = await this.satisfyFeature(
      this.orgId,
      featureFlags.ALLOW_PRIVATE_TEAMS
    )
    let team
    if (this.teamId) {
      // fetch team to get latest details
      team = await this.getTeam({ orgId: this.orgId, teamId: this.teamId })
      this.preferences = { ...team.preferences } || DEFAULT_PREFERENCE
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/forms';
@import '~@/styles/errors';

.access-modifier {
  margin-top: 2px;
}

.track-checkbox {
  background-color: $selectionColor;
  border-radius: 5px;
  padding: 5px 10px;
  border: 1px solid transparent;
}
</style>
